import { useEffect, useState } from "react";
import { X, DownloadSimple } from "@phosphor-icons/react";
import Logo from "./Logo";
import { useBrand } from "../context/BrandContext";
import { InstallGuideSheet, isIos, isAndroid, isStandalone } from "./InstallGuide";

const DISMISS_KEY = "traina_install_dismissed_at";
const DISMISS_TTL_DAYS = 14;
// When Traina ships to the real stores, set these and the banner links there instead
const APPSTORE_URL = process.env.REACT_APP_APPSTORE_URL || "";
const PLAYSTORE_URL = process.env.REACT_APP_PLAYSTORE_URL || "";

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return ts ? Date.now() - ts < DISMISS_TTL_DAYS * 86_400_000 : false;
  } catch {
    return false;
  }
}
function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
}

export default function InstallPrompt() {
  const brand = useBrand();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (!(isIos() || isAndroid()) || isStandalone() || recentlyDismissed()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => { setVisible(false); setDeferredPrompt(null); setGuideOpen(false); };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);

    const t = setTimeout(() => setVisible(true), 1200);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const storeUrl = isIos() ? APPSTORE_URL : PLAYSTORE_URL;

  const act = async () => {
    if (storeUrl) {
      window.open(storeUrl, "_blank", "noopener");
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice.catch(() => ({ outcome: "dismissed" }));
      if (choice.outcome === "accepted") setVisible(false);
      else dismiss();
      return;
    }
    setGuideOpen(true);
  };
  const dismiss = () => { markDismissed(); setVisible(false); setGuideOpen(false); };

  if (!visible) return null;

  const name = brand?.brand_name || "Traina";
  const accent = brand?.brand_accent_color || "#2E5BA8";

  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 z-[60] bg-[#0A0A0A]/97 backdrop-blur border-b shadow-lg"
        style={{ borderColor: `${accent}55`, paddingTop: "env(safe-area-inset-top)" }}
        data-testid="install-banner"
      >
        <div className="flex items-center gap-3 px-3 py-2.5 max-w-[1100px] mx-auto">
          <button onClick={dismiss} className="text-zinc-500 hover:text-white shrink-0 p-1" aria-label="Dismiss" data-testid="install-banner-dismiss">
            <X size={16} weight="bold" />
          </button>
          <div className="h-10 w-10 shrink-0 rounded-xl border border-white/15 bg-[#141414] flex items-center justify-center overflow-hidden">
            {brand?.brand_logo_b64 ? (
              <img src={brand.brand_logo_b64} alt="" className="h-8 w-8 object-contain" />
            ) : (
              <Logo size={30} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm leading-tight truncate text-white">{name}</div>
            <div className="text-[10px] text-zinc-400 leading-tight">{storeUrl ? "Free — on the App Store" : "Add the app to your phone — free"}</div>
          </div>
          <button
            onClick={act}
            className="shrink-0 text-[11px] font-bold uppercase tracking-wider text-white px-4 py-2 rounded-full flex items-center gap-1.5"
            style={{ background: accent }}
            data-testid="install-banner-cta"
          >
            <DownloadSimple size={13} weight="bold" /> {storeUrl ? "Get" : "Install"}
          </button>
        </div>
      </div>

      <InstallGuideSheet open={guideOpen} onClose={() => setGuideOpen(false)} />
    </>
  );
}
